/**
 * csFixationsApi — RTK Query slice cho /purchase/cs-fixations/*.
 * Tag enhanced: 'CSFixation'.
 *
 * Endpoints (Sprint 5):
 *   GET    /purchase/cs-fixations/list?p_contract_no=&page=&page_size=
 *   POST   /purchase/cs-fixations/create
 *   GET    /purchase/cs-fixations/{id}
 *   PUT    /purchase/cs-fixations/{id}
 *   DELETE /purchase/cs-fixations/{id}
 *   GET    /purchase/cs-fixations/{id}/print
 */
import { baseApi } from './baseApi'
import type {
  CSFixationCreateIn,
  CSFixationListQuery,
  CSFixationOut,
  CSFixationUpdateIn,
  DataResponse,
  LocalPPrintResult,
  MessageResponse,
  PageResponse,
} from '@/types/api'

const csFixEnhanced = baseApi.enhanceEndpoints({ addTagTypes: ['CSFixation'] })

export const csFixationsApi = csFixEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listCSFixations: b.query<PageResponse<CSFixationOut>, CSFixationListQuery | void>({
      query: (params) => ({ url: '/purchase/cs-fixations/list', params: params ?? {} }),
      providesTags: (res, _e, arg) => {
        const key =
          (arg && ((arg as CSFixationListQuery).p_contract_no ||
            (arg as CSFixationListQuery).contract_no)) || 'ALL'
        const list = res
          ? [
              ...res.items.map((it) => ({
                type: 'CSFixation' as const,
                id: it.cs_fixation_id,
              })),
              { type: 'CSFixation' as const, id: `LIST-${key}` },
            ]
          : [{ type: 'CSFixation' as const, id: `LIST-${key}` }]
        return [...list, { type: 'CSFixation' as const, id: 'LIST' }]
      },
    }),
    getCSFixation: b.query<DataResponse<CSFixationOut>, number>({
      query: (id) => `/purchase/cs-fixations/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'CSFixation', id }],
    }),
    createCSFixation: b.mutation<DataResponse<CSFixationOut>, CSFixationCreateIn>({
      query: (body) => ({
        url: '/purchase/cs-fixations/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'CSFixation', id: 'LIST' }],
    }),
    updateCSFixation: b.mutation<
      DataResponse<CSFixationOut>,
      { id: number; body: CSFixationUpdateIn }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/cs-fixations/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'CSFixation', id: arg.id },
        { type: 'CSFixation', id: 'LIST' },
      ],
    }),
    deleteCSFixation: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/cs-fixations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'CSFixation', id },
        { type: 'CSFixation', id: 'LIST' },
      ],
    }),
    printCSFixation: b.query<DataResponse<LocalPPrintResult>, number>({
      query: (id) => `/purchase/cs-fixations/${id}/print`,
    }),
  }),
})

export const {
  useListCSFixationsQuery,
  useGetCSFixationQuery,
  useCreateCSFixationMutation,
  useUpdateCSFixationMutation,
  useDeleteCSFixationMutation,
  useLazyPrintCSFixationQuery,
} = csFixationsApi
